import React from 'react';
import styles from './SentimentAnalysisPanel.module.css';

interface Doelgroep {
  naam?: string;
  bron?: string;
  hoofdthema?: string;
}

interface VoiceCategory {
  count?: number;
  examples?: string[];
  actionNeeded?: string;
  doelgroepen?: Doelgroep[];
}

interface SentimentAnalysisData {
  overallEscalationLevel?: number;
  escalatieVerklaring?: string | null;
  topIssues?: { issue: string; framing?: string; severity?: string; doelgroep?: string }[];
  voiceCategories?: {
    constructivelyCritical?: VoiceCategory;
    emotionalPrincipled?: VoiceCategory;
    factualInforming?: VoiceCategory;
    supportive?: VoiceCategory;
    constructivePositive?: VoiceCategory;
    enthusiasticConfirming?: VoiceCategory;
    acknowledgingRealistic?: VoiceCategory;
    ambassadorBehavior?: VoiceCategory;
    factualInformingPositive?: VoiceCategory;
  };
  misinformation?: string[];
  narrativeAnalysis?: string;
  strategicAdvice?: {
    webcareTone?: string;
    stakeholderOutreach?: string;
    messagingAdjustment?: string;
  };
  relevantieFeedback?: { uitgefilterd?: number; reden?: string } | null;
  lastAnalyzedAt?: string;
}

interface SentimentAnalysisPanelProps {
  data: SentimentAnalysisData;
  onVoiceCategoryClick?: (categoryKey: string, categoryLabel: string) => void;
}

const getEscalationColor = (level: number): string => {
  if (level <= 1) return '#4CAF50';
  if (level <= 2) return '#8BC34A';
  if (level <= 3) return '#FF9800';
  if (level <= 4) return '#F44336';
  return '#9C27B0';
};

const getEscalationLabel = (level: number): string => {
  if (level <= 1) return 'Laag';
  if (level <= 2) return 'Beperkt';
  if (level <= 3) return 'Gemiddeld';
  if (level <= 4) return 'Hoog';
  return 'Kritiek';
};

const getSeverityStyle = (severity?: string): { bg: string; color: string } => {
  switch (severity?.toLowerCase()) {
    case 'high':
    case 'hoog':
      return { bg: '#FFEBEE', color: '#C62828' };
    case 'medium':
    case 'gemiddeld':
      return { bg: '#FFF3E0', color: '#EF6C00' };
    case 'low':
    case 'laag':
      return { bg: '#E8F5E9', color: '#2E7D32' };
    default:
      return { bg: '#F0F0F0', color: '#666' };
  }
};

const voiceCategoryLabels: Record<string, { label: string; color: string; group: 'negative' | 'positive' }> = {
  // Negatieve/kritische stemmen
  constructivelyCritical: { label: 'Constructief Kritisch', color: '#FF9800', group: 'negative' },
  emotionalPrincipled: { label: 'Emotioneel / Principieel', color: '#F44336', group: 'negative' },
  factualInforming: { label: 'Feitelijk / Informerend', color: '#607D8B', group: 'negative' },
  supportive: { label: 'Ondersteunend', color: '#8BC34A', group: 'negative' },
  // Positieve stemmen
  constructivePositive: { label: 'Constructief Positief', color: '#4CAF50', group: 'positive' },
  enthusiasticConfirming: { label: 'Enthousiast / Bevestigend', color: '#00BCD4', group: 'positive' },
  acknowledgingRealistic: { label: 'Erkennend / Realistisch', color: '#3F51B5', group: 'positive' },
  ambassadorBehavior: { label: 'Ambassadeursgedrag', color: '#9C27B0', group: 'positive' },
  factualInformingPositive: { label: 'Feitelijk / Informerend Positief', color: '#009688', group: 'positive' }
};

export const SentimentAnalysisPanel: React.FC<SentimentAnalysisPanelProps> = ({ data, onVoiceCategoryClick }) => {
  const escalationLevel = data.overallEscalationLevel || 1;
  const escalationColor = getEscalationColor(escalationLevel);

  return (
    <div className={styles.panel}>
      {/* Escalation Meter */}
      <div className={styles.escalationSection}>
        <div className={styles.escalationHeader}>
          <h3>Escalatie-indicator</h3>
          <span
            className={styles.escalationLevel}
            style={{ backgroundColor: escalationColor }}
          >
            {escalationLevel}/5 — {getEscalationLabel(escalationLevel)}
          </span>
        </div>
        <div className={styles.escalationMeter}>
          <div
            className={styles.escalationFill}
            style={{
              width: `${(escalationLevel / 5) * 100}%`,
              background: `linear-gradient(90deg, #4CAF50 0%, ${escalationColor} 100%)`
            }}
          />
        </div>
        <div className={styles.escalationLabels}>
          <span>Rustig</span>
          <span>Gemiddeld</span>
          <span>Kritiek</span>
        </div>
        {data.escalatieVerklaring && (
          <div className={styles.escalatieVerklaring}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FF9800" strokeWidth="2" strokeLinecap="round">
              <circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" />
            </svg>
            <span>{data.escalatieVerklaring}</span>
          </div>
        )}
      </div>

      {/* Top Issues */}
      {data.topIssues && data.topIssues.length > 0 && (
        <div className={styles.issuesSection}>
          <h3 className={styles.sectionTitle}>Top Issues</h3>
          <div className={styles.issueGrid}>
            {data.topIssues.map((issue, i) => {
              const sevStyle = getSeverityStyle(issue.severity);
              return (
                <div key={i} className={styles.issueCard}>
                  <h4>
                    {issue.issue}
                    {issue.severity && (
                      <span
                        className={styles.severityBadge}
                        style={{ background: sevStyle.bg, color: sevStyle.color }}
                      >
                        {issue.severity}
                      </span>
                    )}
                    {issue.doelgroep && (
                      <span className={styles.doelgroepBadge}>
                        {issue.doelgroep}
                      </span>
                    )}
                  </h4>
                  {issue.framing && <p>{issue.framing}</p>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Voice Categories */}
      {data.voiceCategories && Object.keys(data.voiceCategories).length > 0 && (
        <div className={styles.voiceSection}>
          <h3 className={styles.sectionTitle}>Stemcategorieën</h3>

          {/* Negatieve/Kritische stemmen */}
          {(() => {
            const negativeEntries = Object.entries(data.voiceCategories).filter(
              ([key, cat]) => cat && (cat as VoiceCategory).count && (cat as VoiceCategory).count! > 0 && voiceCategoryLabels[key]?.group === 'negative'
            );
            return negativeEntries.length > 0 ? (
              <>
                <h4 className={styles.voiceGroupTitle}>Kritische stemmen</h4>
                <div className={styles.voiceCategoryGrid}>
                  {negativeEntries.map(([key, category]) => {
                    if (!category) return null;
                    const cat = category as VoiceCategory;
                    const meta = voiceCategoryLabels[key] || { label: key, color: '#999', group: 'negative' };
                    return (
                      <div
                        key={key}
                        className={`${styles.voiceCard} ${onVoiceCategoryClick ? styles.voiceCardClickable : ''}`}
                        style={{ borderTopColor: meta.color }}
                        onClick={() => onVoiceCategoryClick?.(key, meta.label)}
                        role={onVoiceCategoryClick ? 'button' : undefined}
                        tabIndex={onVoiceCategoryClick ? 0 : undefined}
                      >
                        <div className={styles.voiceCardHeader}>
                          <h4>{meta.label}</h4>
                          {cat.count !== undefined && (
                            <span className={styles.voiceCount}>{cat.count}</span>
                          )}
                        </div>
                        {/* Doelgroep koppeling */}
                        {cat.doelgroepen && cat.doelgroepen.length > 0 && (
                          <div className={styles.doelgroepenList}>
                            {cat.doelgroepen.map((dg, i) => (
                              <div key={i} className={styles.doelgroepItem}>
                                {dg.naam && <span className={styles.doelgroepNaam}>{dg.naam}</span>}
                                {dg.hoofdthema && <span className={styles.doelgroepThema}>{dg.hoofdthema}</span>}
                                {dg.bron && <span className={styles.doelgroepBron}>via {dg.bron}</span>}
                              </div>
                            ))}
                          </div>
                        )}
                        {cat.examples && cat.examples.length > 0 && (
                          <div className={styles.voiceQuotes}>
                            {cat.examples.slice(0, 3).map((ex, i) => (
                              <blockquote key={i} className={styles.voiceQuote}>"{ex}"</blockquote>
                            ))}
                          </div>
                        )}
                        {cat.actionNeeded && (
                          <div className={styles.voiceAction}>
                            {cat.actionNeeded}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            ) : null;
          })()}

          {/* Positieve stemmen — alleen labels met daadwerkelijke berichten */}
          {(() => {
            const positiveEntries = Object.entries(data.voiceCategories).filter(
              ([key, cat]) => cat && (cat as VoiceCategory).count && (cat as VoiceCategory).count! > 0 && voiceCategoryLabels[key]?.group === 'positive'
            );
            return positiveEntries.length > 0 ? (
              <>
                <h4 className={styles.voiceGroupTitle}>Positieve stemmen</h4>
                <div className={styles.voiceCategoryGrid}>
                  {positiveEntries.map(([key, category]) => {
                    if (!category) return null;
                    const cat = category as VoiceCategory;
                    const meta = voiceCategoryLabels[key] || { label: key, color: '#999', group: 'positive' };
                    return (
                      <div
                        key={key}
                        className={`${styles.voiceCard} ${onVoiceCategoryClick ? styles.voiceCardClickable : ''}`}
                        style={{ borderTopColor: meta.color }}
                        onClick={() => onVoiceCategoryClick?.(key, meta.label)}
                        role={onVoiceCategoryClick ? 'button' : undefined}
                        tabIndex={onVoiceCategoryClick ? 0 : undefined}
                      >
                        <div className={styles.voiceCardHeader}>
                          <h4>{meta.label}</h4>
                          {cat.count !== undefined && (
                            <span className={styles.voiceCount}>{cat.count}</span>
                          )}
                        </div>
                        {cat.examples && cat.examples.length > 0 && (
                          <div className={styles.voiceQuotes}>
                            {cat.examples.slice(0, 3).map((ex, i) => (
                              <blockquote key={i} className={styles.voiceQuote}>"{ex}"</blockquote>
                            ))}
                          </div>
                        )}
                        {cat.actionNeeded && (
                          <div className={styles.voiceAction}>
                            {cat.actionNeeded}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            ) : null;
          })()}
        </div>
      )}

      {/* Narrative Analysis */}
      {data.narrativeAnalysis && (
        <div className={styles.narrativeSection}>
          <h3>Narratief & Framing</h3>
          <p className={styles.narrativeText}>{data.narrativeAnalysis}</p>
        </div>
      )}

      {/* Misinformation Alerts */}
      {data.misinformation && data.misinformation.length > 0 && (
        <div className={styles.misinformationSection}>
          <h3 className={styles.sectionTitle}>Misinformatie-signalen</h3>
          {data.misinformation.map((item, i) => (
            <div key={i} className={styles.misinformationAlert}>
              <span className={styles.alertIcon}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </span>
              <p>{item}</p>
            </div>
          ))}
        </div>
      )}

      {/* Strategic Advice */}
      {data.strategicAdvice && (
        <div className={styles.adviceSection}>
          <h3 className={styles.sectionTitle}>Strategisch Advies</h3>
          <div className={styles.adviceGrid}>
            {data.strategicAdvice.webcareTone && (
              <div className={styles.adviceCard}>
                <h4>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1E3A2F" strokeWidth="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                  Webcare Toon
                </h4>
                <p>{data.strategicAdvice.webcareTone}</p>
              </div>
            )}
            {data.strategicAdvice.stakeholderOutreach && (
              <div className={styles.adviceCard}>
                <h4>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1E3A2F" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                  Stakeholder Outreach
                </h4>
                <p>{data.strategicAdvice.stakeholderOutreach}</p>
              </div>
            )}
            {data.strategicAdvice.messagingAdjustment && (
              <div className={styles.adviceCard}>
                <h4>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1E3A2F" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                  Messaging Aanpassing
                </h4>
                <p>{data.strategicAdvice.messagingAdjustment}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Relevantie Feedback */}
      {data.relevantieFeedback && data.relevantieFeedback.uitgefilterd && data.relevantieFeedback.uitgefilterd > 0 && (
        <div className={styles.relevantieFeedback}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#607D8B" strokeWidth="2" strokeLinecap="round">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
          </svg>
          <span>
            <strong>{data.relevantieFeedback.uitgefilterd} bericht{data.relevantieFeedback.uitgefilterd !== 1 ? 'en' : ''}</strong> als irrelevant gemarkeerd
            {data.relevantieFeedback.reden && <> — {data.relevantieFeedback.reden}</>}
          </span>
        </div>
      )}

      {/* Last Analyzed */}
      {data.lastAnalyzedAt && (
        <div className={styles.lastAnalyzed}>
          Laatst geanalyseerd: {new Date(data.lastAnalyzedAt).toLocaleDateString('nl-NL', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </div>
      )}
    </div>
  );
};